import { IKey } from '@/common/constants/ikey'
import { collectFormLayoutElements } from '@/common/utils/validateFormElementNames'
import { mergePageFieldMaps } from '@/common/utils/mergePageFieldMaps'
import {
  mergePageDataFieldSnapshot,
  resolveJsResultChangedDataFieldMap,
  sanitizeClientDataFieldMap,
} from '@/common/utils/resolveClientDataActualFields'

/** Платформенные PK / event — не прикладные имена из meta. */
export const LAYOUT_IDENTITY_FIELD_KEYS = new Set([
  '_id',
  '_typeId',
  '_versionId',
  'preOpenId',
  '_event',
])

const PLATFORM_FIELD_SUFFIXES = [
  IKey.LABEL_DATA,
  IKey.TT_SEARCH,
  IKey.TT_NEXT_SEARCH_POSITION,
  IKey.SHOW_MESSAGE_DIALOG,
  IKey.TABLE_SELECTED_ROW,
  IKey.PAGE_RELOAD,
  IKey.SELECTED,
  IKey.ROW_CHECKED,
  IKey.ALTERNATIVES_SUFFIX,
  IKey.ACTION,
  '__page',
  '__color',
  '__description',
  '__search',
  '__listbox',
  '__label_data',
] as const

function resolveFormLayoutRoot(formRoot: unknown): unknown {
  if (!formRoot || typeof formRoot !== 'object') return formRoot
  const root = formRoot as Record<string, unknown>
  if (root.panels != null || root.actions != null || root.main != null) {
    return root
  }
  const nested = root.form
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return nested
  }
  return root
}

/** Имена полей элементов из XML-разметки текущей формы. */
export function collectLayoutFieldNames(formRoot: unknown): Set<string> {
  const names = new Set<string>()
  for (const element of collectFormLayoutElements(formRoot)) {
    const name = String(element.name ?? '').trim()
    if (name) {
      names.add(name)
    }
  }
  return names
}

function isPlatformSuffixKey(key: string, layoutNames: Set<string>): boolean {
  for (const suffix of PLATFORM_FIELD_SUFFIXES) {
    if (key.endsWith(suffix)) {
      const base = key.slice(0, key.length - suffix.length)
      if (base === '' || layoutNames.has(base)) {
        return true
      }
    }
  }
  return false
}

/** Поле допустимо в исходящем currentData / layout-scoped getForm base. */
export function isLayoutScopedFieldKey(key: string, layoutNames: Set<string>): boolean {
  const normalized = String(key ?? '').trim()
  if (!normalized) return false
  if (LAYOUT_IDENTITY_FIELD_KEYS.has(normalized)) return true
  if (layoutNames.has(normalized)) return true
  if (normalized.startsWith('_')) return true
  if (isPlatformSuffixKey(normalized, layoutNames)) return true
  const sep = normalized.indexOf('__')
  if (sep > 0) {
    const base = normalized.slice(0, sep)
    if (layoutNames.has(base)) return true
  }
  return false
}

function mergePageFieldsWithoutChain(pageData: unknown): Record<string, unknown> {
  if (pageData == null || typeof pageData !== 'object') {
    return {}
  }
  const pd = pageData as Record<string, unknown>
  const initial = (pd.uniData as { fields?: { fields?: Record<string, unknown> } } | undefined)?.fields
    ?.fields
  const changed = resolveJsResultChangedDataFieldMap(
    (pd.jsResult as { changed_data?: unknown } | undefined)?.changed_data,
  )
  return sanitizeClientDataFieldMap(mergePageFieldMaps(initial, changed))
}

export function resolveFormLevelClientFields(pageData: unknown): Record<string, unknown> {
  return mergePageDataFieldSnapshot(pageData) as Record<string, unknown>
}

/**
 * Снимок полей для action currentData / getForm: uniData + changed_data,
 * без currentChainData; только ключи текущей layout-разметки + platform ids.
 */
export function resolveLayoutScopedClientFields(
  pageData: unknown,
  formRoot?: unknown,
): Record<string, unknown> {
  const merged = mergePageFieldsWithoutChain(pageData)
  const layoutRoot = resolveFormLayoutRoot(formRoot ?? (pageData as Record<string, unknown>)?.form)
  const layoutNames = collectLayoutFieldNames(layoutRoot)
  if (layoutNames.size === 0) {
    return merged
  }

  const scoped: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(merged)) {
    if (isLayoutScopedFieldKey(key, layoutNames)) {
      scoped[key] = value
    }
  }
  return scoped
}

/** Имена полей элементов из XML-разметки только для указанных panelId. */
export function collectLayoutFieldNamesForPanels(
  formRoot: unknown,
  panelIds: Set<string>,
): Set<string> {
  const names = new Set<string>()
  if (panelIds.size === 0) {
    return names
  }
  for (const element of collectFormLayoutElements(formRoot)) {
    const panelId = String(element.panelId ?? '').trim()
    if (panelId && panelIds.has(panelId)) {
      const name = String(element.name ?? '').trim()
      if (name) {
        names.add(name)
      }
    }
  }
  return names
}

export function collectPagePanelIds(formRoot: unknown, pageId: string | number): Set<string> {
  const panelIds = new Set<string>()
  const pageIdStr = String(pageId ?? '').trim()
  if (!pageIdStr) {
    return panelIds
  }

  const form = resolveFormLayoutRoot(formRoot) as Record<string, unknown> | null
  if (!form) {
    return panelIds
  }

  const extractPanelsFromPage = (page: unknown) => {
    if (!page || typeof page !== 'object') return
    const pageRecord = page as Record<string, unknown>
    const id = String(pageRecord.id ?? '').trim()
    if (id !== pageIdStr && id !== String(pageId)) {
      return
    }

    const panelRows = pageRecord.panelRows
    if (!Array.isArray(panelRows)) {
      return
    }

    for (const row of panelRows) {
      if (!Array.isArray(row)) continue
      for (const cell of row) {
        if (!cell || typeof cell !== 'object') continue
        const cellRecord = cell as Record<string, unknown>
        const panelId = String(
          cellRecord.panelId ??
            (cellRecord._attributes as { id?: unknown } | undefined)?.id ??
            cellRecord.id ??
            '',
        ).trim()
        if (panelId) {
          panelIds.add(panelId)
        }
      }
    }
  }

  const main = form.main
  if (Array.isArray(main)) {
    for (const mainTab of main) {
      if (Array.isArray(mainTab)) {
        for (const page of mainTab) {
          extractPanelsFromPage(page)
        }
      } else {
        extractPanelsFromPage(mainTab)
      }
    }
  }

  return panelIds
}

export function filterLayoutScopedFieldsToPanels(
  fields: Record<string, unknown>,
  formRoot: unknown,
  panelIds: Set<string>,
): Record<string, unknown> {
  const layoutNames = collectLayoutFieldNamesForPanels(formRoot, panelIds)
  if (layoutNames.size === 0) {
    return {}
  }

  const scoped: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(fields)) {
    if (isLayoutScopedFieldKey(key, layoutNames)) {
      scoped[key] = value
    }
  }
  return scoped
}
